import { useEffect, useState } from "react";
import { Image as AntImage, message, Button, Tooltip, Popconfirm } from "antd";
import {
  CloseOutlined,
  LoadingOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { remove } from "ramda";
import { useDropzone } from "react-dropzone";
import config from "@config";
import SortableList, { SortableItem } from "react-easy-sort";
import { arrayMoveImmutable } from "array-move";
import * as API from "@api";

export default function Widget({ input, onChange, data }) {
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState(data?.data?.[input.key] || []);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: loading || photos.length >= 25,
    accept: {
      "image/png": [".png"],
      "image/jpg": [".jpeg", ".jpg"],
    },
    onDrop: async (files) => {
      // Ensure array
      if (!Array.isArray(files)) {
        files = [files];
      }

      if (files.length + photos.length > 25) {
        return message.error("Total number of images should not exceed 25");
      }

      files.map((file) => {
        if (file.size > 5 * 1000 * 1000) {
          return message.error(
            "Image size should not exceed 5MB, please try again with a smaller image"
          );
        }
      });

      try {
        setLoading(true);
        const _res = await API.Static.uploadImages({
          files,
        });

        if (!_res.data || _res.error) {
          throw new Error();
        }

        setLoading(false);
        setPhotos([...photos, ..._res.data.map((file) => file.name)]);
        return message.success("Images uploaded");
      } catch (error) {
        setLoading(false);
        return message.error(
          `Could not upload images, please try again later.`,
          2
        );
      }
    },
  });

  useEffect(() => {
    onChange(input.key, photos);
  }, [photos]);

  const onSortEnd = (oldIndex, newIndex) => {
    setPhotos((array) => arrayMoveImmutable(array, oldIndex, newIndex));
  };

  const onImageDelete = (idx) => {
    setPhotos(remove(idx, 1, photos));
  };

  return (
    <div className="col-span-12">
      <span className="block text-xs font-medium text-neutral-800 mb-2">
        Upload Images{" "}
        {input.required && <i className="not-italic text-rose-600">*</i>}
      </span>
      <SortableList
        onSortEnd={onSortEnd}
        className="flex flex-wrap select-none justify-start gap-2"
        draggedItemClassName="dragging-item"
      >
        {photos.map((item, key) => (
          <SortableItem key={item}>
            <div className="draggable-item">
              <AntImage
                width="100%"
                height="100%"
                src={`${config.api}/static/img/thumb-${item}`}
                preview={{
                  src: `${config.api}/static/img/${item}`,
                }}
                className="object-contain object-center"
              />
              <Popconfirm
                title="Delete image"
                okText="Yes"
                cancelText="Cancel"
                onConfirm={() => onImageDelete(key)}
              >
                <Tooltip placement="bottom" title="Delete Image">
                  <Button
                    size="small"
                    className="absolute top-2 right-2"
                    danger
                    type="primary"
                    shape="circle"
                    icon={<CloseOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            </div>
          </SortableItem>
        ))}
      </SortableList>

      <div
        className="ant-upload ant-upload-drag cursor-pointer hover:border-primary transition-long hover:transition-long bg-neutral-50 border-dashed border border-neutral-300 rounded-md m-0 mt-1 py-8"
        {...getRootProps()}
      >
        <input {...getInputProps()} accept=".png, .jpg, .jpeg" />
        <p className="mx-auto block w-fit text-5xl mb-3 text-primary">
          {loading ? <LoadingOutlined /> : <PictureOutlined />}
        </p>
        <p className="text-sm text-black w-full block text-center">
          Click or drag file to this area to upload
        </p>
        <p className="text-xs text-neutral-500 w-full block text-center mt-2">
          Max file size: 5MB. Up to 25 images.
        </p>
      </div>
    </div>
  );
}
